import { DateTime } from "luxon";
import {
  MONTH_FULL_NAMES,
  MONTH_NAMES,
  MONTH_NUMBER_TO_LABEL_MAPPER,
} from "@/constants";

const currentYear = DateTime.now().year;
export const DOBYears = Array.from({ length: 125 }, (_, i) => currentYear - i);

/**
 * Converts an ISO date string to a human-readable format
 *
 * @param isoDate - The date string in ISO format (YYYY-MM-DD), e.g., "2025-01-01"
 * @param format - The desired output format:
 *   - 'long': Full month name (e.g., "January 1, 2025")
 *   - 'medium': Abbreviated month (e.g., "Jan 1, 2025")
 *   - 'short': Numeric format (e.g., "1/1/2025")
 *   Defaults to 'long' if not specified
 * @returns The formatted date string in the specified format
 *
 * @example
 * formatDate("2025-01-01")          // "January 1, 2026"
 * formatDate("2025-01-01", "medium") // "Jan 1, 2026"
 * formatDate("2025-01-01", "short")  // "1/1/2026"
 */

export function formatDate(
  dateString: string | undefined,
  format: "long" | "medium" | "short" = "long",
  locale: "en-US" | "en-GB" = "en-US",
): string {
  const iso = normalizeToISODate(dateString);
  if (!iso) return "";

  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  const formats = {
    long: { year: "numeric", month: "long", day: "2-digit" },
    medium: { year: "numeric", month: "short", day: "2-digit" },
    short: { year: "numeric", month: "2-digit", day: "2-digit" },
  } as const;

  return date.toLocaleDateString(locale, formats[format]);
}

// ------------------------------------------

/** Timezone-independent "YYYY-MM-DD" for a date string (time suffix ignored) or a Date (local day). */
export function normalizeToISODate(
  dateString: string | Date | undefined,
): string {
  if (!dateString) return "";

  if (dateString instanceof Date) {
    if (isNaN(dateString.getTime())) return "";
    const mm = String(dateString.getMonth() + 1).padStart(2, "0");
    const dd = String(dateString.getDate()).padStart(2, "0");
    return `${dateString.getFullYear()}-${mm}-${dd}`;
  }

  const parts = dateString.trim().split(/[T\s]/)[0].split(/[-/]/).map(Number);

  if (parts.length !== 3 || parts.some(isNaN)) return "";

  let year: number, month: number, day: number;

  // Detect YYYY-MM-DD
  if (String(parts[0]).length === 4) {
    [year, month, day] = parts;
  }
  // Assume MM-DD-YYYY or MM/DD/YYYY
  else {
    [month, day, year] = parts;
  }

  // Pad month/day to 2 digits
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");

  return `${year}-${mm}-${dd}`;
}

export const DOBMonths = Array.from({ length: 12 }, (_, i) =>
  DateTime.fromObject({ month: i + 1 }).toFormat("LLLL"),
);

export const getMonthName = (index: number): string => {
  if (index < 0 || index > 11) {
    // Consider throwing an error for clearer contract, e.g.:
    // throw new Error("Month index must be between 0 and 11.");
    return "Invalid month index";
  }
  // Luxon's months are 1-indexed (1 for January, 12 for December)
  const month = DateTime.fromObject({ month: index + 1 }).toFormat("LLLL");
  return month;
};

export const convertToMinuteAndSecond = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export const formatDateToMMDDYYYY = (
  date?: Date | string,
): string | undefined => {
  if (!date) return undefined;
  const dateObj = new Date(date);
  const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(dateObj.getDate()).padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${month}/${day}/${year}`;
};
// export function formatDateToMMDDYYYY(dateString: string): string {
//   const date = new Date(dateString);
//   const month = (date.getMonth() + 1).toString().padStart(2, "0");
//   const day = date.getDate().toString().padStart(2, "0");
//   const year = date.getFullYear();
//   return `${month}/${day}/${year}`;
// }

/* Converts a date string "YYYY-MM-DD" to format like: "Jan 5, 2024" 
Example: (Input: "2024-01-05" Output: "Jan 5, 2024") */
export function formatDateToLabel(dateStr: string): string {
  const [year, month, day] = dateStr.split("-");
  const monthLabel = MONTH_NUMBER_TO_LABEL_MAPPER[month];
  const dayNum = String(Number(day)); // removes leading zero
  return `${monthLabel} ${dayNum}, ${year}`;
}

/* Converts date string "YYYYMM" to format like: "Jan 2024"
Example: (Input: "202401" Output: "Jan 2024" ) */
export function formatToMonthYr(yyyymm: string): string {
  const year = yyyymm.slice(0, 4);
  const month = parseInt(yyyymm.slice(4, 6), 10) - 1; // 0-based index
  return `${MONTH_FULL_NAMES[month]} ${year}`;
}

/* Converts date string "YYYY-MM-DD" to format like: "06 Jan"
Example: (Input: "2024-01-06" Out: "06 Jan") */
export function formatDateToDayMonth(dateStr: string): string {
  if (typeof dateStr !== "string") return "";
  const s = dateStr.trim();
  const match = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s]|$)/);
  if (!match) return "";
  const [, , monthPart, dayPart] = match;
  const monthIndex = parseInt(monthPart, 10) - 1;
  const dayNum = parseInt(dayPart, 10);
  if (Number.isNaN(monthIndex) || Number.isNaN(dayNum)) return "";
  if (monthIndex < 0 || monthIndex > 11) return "";
  const day = String(dayNum).padStart(2, "0");
  return `${day} ${MONTH_NAMES[monthIndex]}`;
}

export const formatDateTime = (isoString: string): string => {
  return DateTime.fromISO(isoString).toFormat("yyyy-MM-dd HH:mm:ss");
};

export const formatDateHumanReadable = (dateTimeString: string): string => {
  let date: DateTime;
  if (!dateTimeString || typeof dateTimeString !== "string") return "";
  // first case => ISO format
  if (dateTimeString.includes("T")) {
    date = DateTime.fromISO(dateTimeString, { zone: "utc" }).toLocal();
  } else {
    date = DateTime.fromFormat(dateTimeString, "yyyy-MM-dd HH:mm:ss.S", {
      zone: "utc",
    }).toLocal();
  }
  return date.toFormat("dd, MMMM yyyy hh:mm a");
};

export function getTimeOfDay(now: Date) {
  const hour = now.getHours();

  if (hour >= 5 && hour < 12) {
    return "Morning";
  } else if (hour >= 12 && hour < 17) {
    return "Afternoon";
  } else {
    return "Evening";
  }
}

export function formatTimestampToShortDate(timestamp: string): string {
  if (!timestamp || typeof timestamp !== "string") return "";
  const datePart = timestamp.split(" ")[0]; // "2025-08-05"
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return "";
  const monthIndex = Number(m) - 1;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (monthIndex < 0 || monthIndex > 11) return "";
  return `${monthNames[monthIndex]} ${d}, ${y}`;
}

/**
 * Convert YYYYMM, YYYYMMDD, or YYYY-MM-DD into formatted date
 * Examples:
 *   202204      -> "April 2022"
 *   20220415    -> "April 15, 2022"
 *   2024-08-01  -> "August 2024"
 */
/**
 * Parse config date string. YYYY-MM-DD → exact date. YYYYMM → first or last day of month.
 */
export function parseConfigDate(
  value: string | undefined,
  mode: "start" | "end",
): Date | undefined {
  if (!value) return undefined;
  const str = String(value).trim();
  if (str.includes("-")) {
    const iso = normalizeToISODate(str);
    if (iso) {
      const [y, m, d] = iso.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      return normalizeToISODate(date) === iso ? date : undefined;
    }
    const match = str.match(/^(\d{4})-(\d{1,2})$/);
    if (!match) return undefined;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) return undefined;
    return mode === "start"
      ? new Date(year, month - 1, 1)
      : new Date(year, month, 0);
  }
  const s = str.replace(/-/g, "").slice(0, 6);
  if (s.length < 6) return undefined;
  const year = parseInt(s.slice(0, 4), 10);
  const month = parseInt(s.slice(4, 6), 10);
  if (year < 1900 || year > 9999 || month < 1 || month > 12) return undefined;
  return mode === "start"
    ? new Date(year, month - 1, 1)
    : new Date(year, month, 0);
}

/**
 * Returns the earlier of value and today. Use for date pickers to cap future dates.
 */
export function capMaxDate(value: Date | undefined): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!value) return today;

  const input = new Date(value);
  if (isNaN(input.getTime())) return today;

  const inputDay = new Date(
    input.getFullYear(),
    input.getMonth(),
    input.getDate(),
  );
  return inputDay > today ? today : inputDay;
}

/**
 * Cap a "min" date so it never becomes greater than `max`.
 * Useful when your UI expects [min..max] to be a valid range.
 */
export function capMinDate(
  value: Date | undefined,
  max: Date,
): Date | undefined {
  if (!value) return undefined;
  return value > max ? max : value;
}

export function formatYYYYMMDDToDate(value: number | string): string {
  if (!value) return "";

  // Remove dashes if present (YYYY-MM-DD → YYYYMMDD)
  const dateStr = String(value).replace(/-/g, "");

  const year = parseInt(dateStr.slice(0, 4), 10);
  const month = parseInt(dateStr.slice(4, 6), 10);
  const day = dateStr.length === 8 ? parseInt(dateStr.slice(6, 8), 10) : null;

  // Validate
  if (year < 1000 || year > 9999 || month < 1 || month > 12) return "";
  if (day !== null && (day < 1 || day > 31)) return "";

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // If input was YYYY-MM-DD → show only Month Year
  if (String(value).includes("-")) {
    return `${months[month - 1]} ${year}`;
  }

  if (day !== null) {
    return `${months[month - 1]} ${day}, ${year}`;
  }

  return `${months[month - 1]} ${year}`;
}

export function shouldDisableIdRequest(idRequestedAt: string): boolean {
  if (!idRequestedAt) return false;

  const requestedDate = new Date(idRequestedAt).getTime();

  if (isNaN(requestedDate)) {
    console.error("Invalid date format:", idRequestedAt);
    return false;
  }

  const now = Date.now();
  const oneHourInMs = 60 * 60 * 1000;

  return Math.abs(now - requestedDate) <= oneHourInMs;
}
